import { useState } from "react";
import { PlusIcon, PrinterIcon, TrashIcon, WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import {
  IVehicle,
  PartsPriority,
  PartsStatusDropdowns,
  PartsStatusValues,
  VehicleComplexity,
  VehicleStatus,
  YardStatus,
} from "../../models/Vehicle";
import useCrushSummary from "../../data/useCrushSummary";
import useSingleSetting from "../../data/useSingleSetting";
import useLocations from "../../data/useLocations";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { useLabelGenerator } from "../../hooks/useLabelGenerator";
import useStockPermissions from "../../data/useStockPermissions";
import printHtml from "../../utils/printHtml";
import PriceFormat from "../../utils/priceFormat";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import LoadingWheel from "../shared/LoadingWheel";
import OnPrimaryButton from "../shared/Inputs/OnPrimaryButton";
import OnPrimarySelect from "../shared/Inputs/OnPrimarySelect";
import OnPrimaryComboBox from "../shared/Inputs/OnPrimaryComboBox";
import { SearchPaletteItem } from "../shared/Inputs/SearchPaletteTypes";
import BulkAddToWorkOrderModal from "../workOrders/BulkAddToWorkOrderModal";
import useNotification from "../notifications/useNotifications";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";

// Admin toggle (managed in Betty) that stops a vehicle being crushed while parts are
// still on it. Absent, or not visible to the customer, means the rule is off — the API
// 404s both cases and enforces the same rule itself, so failing open here is safe.
const BLOCK_CRUSH_WITH_PARTS_REMAINING_KEY = "Vehicle:BlockCrushWithPartsRemaining";

// Build {id, name} option lists from an enum, dropping its reverse numeric keys.
// The id stays the raw numeric enum index; the name is translated for display.
const enumOptions = (e: object, t: (key: string) => string) =>
  Object.values(e)
    .filter((v) => isNaN(Number(v)))
    .map((name, i) => ({ id: i, name: t(name as string) }));

export default function VehicleQuickActions({
  vehicle,
  onCreateDismantleJob,
}: {
  vehicle: IVehicle;
  /** Switches the vehicle page to the Parts tab where dismantle jobs are created. */
  onCreateDismantleJob?: () => void;
}) {
  const { t } = useTranslation("vehicles");
  const [workOrderOpen, setWorkOrderOpen] = useState(false);
  const [isPrintingVehicleLabel, setIsPrintingVehicleLabel] = useState(false);
  const [crushOpen, setCrushOpen] = useState(false);
  const [crushReason, setCrushReason] = useState("");
  const isTme = CheckFeatureFlag("TME");

  const { locations } = useLocations();
  const { patchVehicle } = useVehiclesMutations();
  // Only fetch the crush decision summary once the confirm modal is opened.
  const { crushSummary } = useCrushSummary(crushOpen ? vehicle.id : undefined);
  const { singleSetting: blockCrushSetting } = useSingleSetting(BLOCK_CRUSH_WITH_PARTS_REMAINING_KEY);
  const { restricted } = useStockPermissions();
  const { generateVehicleLabel } = useLabelGenerator();
  const { addNotification } = useNotification();
  const { saveHandlers: partsStatusHandlers } = useDefaultCRUDHandlers(t("Parts Status"));
  const { saveHandlers: partsPriorityHandlers } = useDefaultCRUDHandlers(t("Priority Status"));
  const { saveHandlers: locationHandlers } = useDefaultCRUDHandlers(t("Vehicle Location"));
  const { saveHandlers: yardStatusHandlers } = useDefaultCRUDHandlers(t("Yard Status"));
  const { saveHandlers: complexityHandlers } = useDefaultCRUDHandlers(t("Vehicle Complexity"));
  const { saveHandlers: crushHandlers } = useDefaultCRUDHandlers(t("Vehicle"));

  const patch = (changes: object, handlers: { onSuccess: () => void; onError: () => void }) =>
    patchVehicle.mutate(
      { vehicleId: vehicle.id, patch: changes },
      { onSuccess: () => handlers.onSuccess(), onError: () => handlers.onError() }
    );

  // Parts Status options come from PartsStatusDropdowns but the value persisted on
  // the vehicle is a PartsStatusValues, so we map between them by display name.
  const partsStatusOptions = Object.values(PartsStatusDropdowns)
    .filter((v) => isNaN(Number(v)))
    .map((name) => ({ id: name as string, name: t(name as string) }));

  const locationOptions = [{ id: "", name: t("No Location") }, ...(locations.data?.data ?? [])];

  const handleLocationUpdate = (locationId: string) => {
    const next = locationId || null;
    if (vehicle.locationId === next) return;
    patch({ locationId: next, updateLocationId: true }, locationHandlers);
  };

  const handlePrintVehicleLabel = async () => {
    setIsPrintingVehicleLabel(true);
    try {
      const html = await generateVehicleLabel(vehicle);
      printHtml(html);
    } catch {
      addNotification({ variant: "error", primaryText: t("Unable to print vehicle label") });
    } finally {
      setIsPrintingVehicleLabel(false);
    }
  };

  const handleCrushConfirm = () => {
    const reason = crushReason.trim();
    patchVehicle.mutate(
      { vehicleId: vehicle.id, patch: { scrap: true, ...(reason ? { scrapReason: reason } : {}) } },
      {
        onSuccess: () => {
          crushHandlers.onSuccess();
          setCrushOpen(false);
          setCrushReason("");
        },
        // The API refuses a blocked crush with a 409 whose message names what's still
        // on the car. Show that instead of a generic "failed to save" — the operator
        // can act on it, and it's the only feedback if the setting loaded late.
        onError: (error: any) => {
          const message = error?.message as string | undefined;
          if (message && !message.startsWith("HTTP Error:")) {
            addNotification({ variant: "error", primaryText: message });
            return;
          }
          crushHandlers.onError();
        },
      }
    );
  };

  const summary = crushSummary.data?.data;

  // Block the crush when the setting is on and parts are still on the car. Anything
  // unresolved — setting absent, still loading, summary unavailable — leaves crushing
  // open; the API guard is the real enforcement.
  const crushBlockEnabled = blockCrushSetting.data?.data?.value?.trim().toLowerCase() === "true";
  // Stock:AdminOnlyChanges blocks it the same way for anyone but an admin - crushing scraps whatever
  // is still on the car.
  const adminOnlyCrush = restricted && !crushBlockEnabled;
  const crushBlocked = (crushBlockEnabled || restricted) && (summary?.remainingParts ?? 0) > 0;

  const linkedName = `${vehicle.vrm ?? t("Unknown VRM")}${vehicle.combinedMake ? ` - ${vehicle.combinedMake}` : ""}${vehicle.combinedModel ? ` ${vehicle.combinedModel}` : ""
    }`;

  const linkedItems: SearchPaletteItem[] = [{ id: vehicle.id, name: linkedName, type: "Vehicle" }];

  return (
    <div className="rounded-lg bg-primary-600 p-4 shadow">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-white/70">{t("Quick Actions")}</h2>

      <BulkAddToWorkOrderModal
        open={workOrderOpen}
        onClose={() => setWorkOrderOpen(false)}
        selectedItems={linkedItems}
      />

      <ConfirmModal
        open={crushOpen}
        setOpen={setCrushOpen}
        title={t("Mark {{vrm}} as Crushed?", { vrm: vehicle.vrm ?? t("vehicle") })}
        message={t("Crushing scraps everything still on the car and archives the vehicle.")}
        confirmButtonText={crushBlocked ? t("Parts still on car") : t("Mark as Crushed")}
        onConfirm={handleCrushConfirm}
        isLoading={patchVehicle.isLoading}
        confirmDisabled={crushBlocked}
      >
        <div className="mt-2 space-y-3 text-left">
          {crushSummary.isLoading ? (
            <LoadingWheel />
          ) : summary ? (
            <div className="space-y-1">
              <p className="text-sm text-gray-700">
                {t("{{removed}} of {{total}} parts removed — {{remaining}} parts still on the car worth {{value}}", {
                  removed: summary.removedParts,
                  total: summary.totalParts,
                  remaining: summary.remainingParts,
                  value: PriceFormat(summary.remainingValue),
                })}
              </p>
              {summary.remainingListedCount > 0 && (
                <p className="text-sm font-medium text-amber-700">
                  {t("{{count}} live listings will be ended", { count: summary.remainingListedCount })}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">{t("Unable to load the crush summary.")}</p>
          )}

          {crushBlocked && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm font-semibold text-red-800">{t("Can't crush yet")}</p>
              <p className="mt-0.5 text-sm text-red-700">
                {adminOnlyCrush
                  ? t(
                    "{{count}} parts are still on the car. Only an admin can crush a vehicle with parts still on it.",
                    { count: summary?.remainingParts ?? 0 }
                  )
                  : t(
                    "{{count}} parts are still on the car. Remove them, or mark them damaged or missing, before crushing.",
                    { count: summary?.remainingParts ?? 0 }
                  )}
              </p>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-gray-500">{t("Crush reason (optional)")}</label>
            <input
              type="text"
              value={crushReason}
              onChange={(e) => setCrushReason(e.target.value)}
              disabled={patchVehicle.isLoading || crushBlocked}
              className="w-full mt-1 px-3 py-2 text-sm text-gray-900 rounded-md border-gray-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <p className="text-sm font-semibold text-red-600">
            {t("Confirming permanently scraps the remaining parts, ends their marketplace listings, and cannot be undone.")}
          </p>
        </div>
      </ConfirmModal>

      <div className="space-y-3">
        <OnPrimaryButton
          label={t("Create Dismantle Job")}
          variant="solid"
          icon={<WrenchScrewdriverIcon className="h-4 w-4" />}
          onClick={() => onCreateDismantleJob?.()}
          disabled={!onCreateDismantleJob}
        />

        <OnPrimaryButton
          label={t("Add to work order")}
          variant="outline"
          icon={<PlusIcon className="h-4 w-4" />}
          onClick={() => setWorkOrderOpen(true)}
        />

        <OnPrimaryButton
          label={t("Print Vehicle Labels")}
          variant="outline"
          icon={<PrinterIcon className="h-4 w-4" />}
          onClick={handlePrintVehicleLabel}
          isLoading={isPrintingVehicleLabel}
        />

        {!isTme && (
          <OnPrimarySelect
            label={t("Parts Status")}
            options={partsStatusOptions}
            value={PartsStatusValues[vehicle.engineExportStatus]}
            onChange={(name) => patch({ engineExportStatus: PartsStatusValues[name] }, partsStatusHandlers)}
            disabled={patchVehicle.isLoading}
          />
        )}
        {!isTme && (
          <OnPrimarySelect
            label={t("Parts Priority")}
            options={enumOptions(PartsPriority, t)}
            value={vehicle.partsPriority}
            onChange={(value) => patch({ partsPriority: value }, partsPriorityHandlers)}
            disabled={patchVehicle.isLoading}
          />
        )}

        <OnPrimaryComboBox
          key={`vehicle-location-${vehicle.id}-${vehicle.locationId ?? "none"}`}
          label={t("Yard Location")}
          options={locationOptions}
          value={vehicle.locationId ?? ""}
          onChange={handleLocationUpdate}
          placeholder={t("No Location")}
          disabled={locations.isLoading || patchVehicle.isLoading}
        />

        {!isTme && (
          <OnPrimarySelect
            label={t("Yard Status")}
            options={enumOptions(YardStatus, t)}
            value={vehicle.yardStatus}
            onChange={(value) => patch({ yardStatus: value }, yardStatusHandlers)}
            disabled={patchVehicle.isLoading}
          />
        )}

        <OnPrimarySelect
          label={t("Complexity")}
          options={enumOptions(VehicleComplexity, t)}
          value={vehicle.complexity}
          onChange={(value) => patch({ complexity: value }, complexityHandlers)}
          disabled={patchVehicle.isLoading}
        />

        {vehicle.status !== VehicleStatus.Scrapped && (
          <OnPrimaryButton
            label={t("Mark as Crushed")}
            variant="outline"
            icon={<TrashIcon className="h-4 w-4" />}
            onClick={() => setCrushOpen(true)}
            classes="!bg-red-600 hover:!bg-red-700 !border-red-500"
          />
        )}

      </div>
    </div>
  );
}
